import React, { useState, useMemo } from 'react';
import {
  X, FlaskConical, CheckCircle, Clock, Play,
  BarChart3, Eye, Mail, Target,
  AlertTriangle
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { ABTestStatusBadge, ABTestResultsSummary } from './ABTestIntegration';
import AIAgentDecisionTrail from './AIAgentDecisionTrail';
import { EmptyState } from 'components/ui/empty-state';
import { cn } from 'lib/utils';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from 'components/ui/select';

const CampaignABTestModal = ({
  isOpen,
  onClose,
  campaign,
  abTests = [],
  aiAgents = [],
  decisions = [],
  collaborations = [],
  onCreateTest,
  onViewTestDetails,
  onApproveDecision,
  onRejectDecision,
  onImplementWinner
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTestConfig, setNewTestConfig] = useState({
    name: '',
    type: 'subject_line',
    description: '',
    trafficSplit: [50, 50],
    duration: 14,
    successMetric: 'open_rate'
  });

  // Filter tests for this campaign
  const campaignTests = useMemo(() => {
    return abTests.filter(test =>
      test.templateId === campaign.templateId || test.campaignId === campaign.id
    );
  }, [abTests, campaign]);

  const activeTests = campaignTests.filter(test => test.status === 'running');
  const completedTests = campaignTests.filter(test => test.status === 'completed');
  const draftTests = campaignTests.filter(test => test.status === 'draft');
  const awaitingReviewTests = campaignTests.filter(test => test.status === 'awaiting_review');

  // Filter decisions for this campaign
  const campaignDecisions = decisions.filter(decision =>
    decision.campaignId === campaign.id || decision.templateId === campaign.templateId
  );

  const pendingDecisions = campaignDecisions.filter(d => d.status === 'pending');

  const handleCreateTest = async () => {
    try {
      const testData = {
        ...newTestConfig,
        campaignId: campaign.id,
        templateId: campaign.templateId,
        name: newTestConfig.name || `${campaign.name} - ${newTestConfig.type.replace('_', ' ')} Test`
      };

      await onCreateTest(testData);
      setShowCreateForm(false);
      setNewTestConfig({
        name: '',
        type: 'subject_line',
        description: '',
        trafficSplit: [50, 50],
        duration: 14,
        successMetric: 'open_rate'
      });
    } catch (error) {
      console.error('Error creating A/B test:', error);
    }
  };

  const getTestTypeOptions = () => [
    { value: 'subject_line', label: 'Subject Line', description: 'Test different email subject lines' },
    { value: 'cta_button', label: 'CTA Button', description: 'Test call-to-action button text and design' },
    { value: 'content', label: 'Email Content', description: 'Test different email body content' },
    { value: 'send_time', label: 'Send Time', description: 'Test optimal sending times' },
    { value: 'from_name', label: 'From Name', description: 'Test different sender names' },
    { value: 'preheader', label: 'Preheader Text', description: 'Test email preheader variations' }
  ];

  const getSuccessMetricOptions = () => [
    { value: 'open_rate', label: 'Open Rate', description: 'Measure email opens' },
    { value: 'click_rate', label: 'Click Rate', description: 'Measure link clicks' },
    { value: 'conversion_rate', label: 'Conversion Rate', description: 'Measure goal completions' },
    { value: 'revenue', label: 'Revenue', description: 'Measure revenue generated' }
  ];

  /*
   * On the shared Dialog rather than a hand-rolled `fixed inset-0 … z-50`.
   * This opens from a button inside CampaignDetailsModal, which is a Dialog at
   * z-[110] over a z-[100] overlay — so at z-50 this rendered *underneath* the
   * modal that launched it. Radix also brings what the hand-rolled version
   * never had: a focus trap, Escape to close, scroll lock, and correct
   * stacking when opened on top of another dialog.
   */
  return (
    <Dialog open={isOpen} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent
        size="xl"
        padding={false}
        showClose={false}
        aria-labelledby="ab-test-modal-title"
        className={cn(
          /* A fixed frame, stepped per breakpoint, rather than a panel that
             sizes to whatever the active tab happens to contain. Overview with
             no tests, Overview with six, Results and AI Decisions all have very
             different heights, so a content-sized modal jumped and re-centred
             on every tab click. The frame now holds still and the body scrolls
             inside it.

             Below md the viewport is the constraint, so it fills it minus a
             margin. From md up it is a real fixed size. */
          "w-[calc(100vw-1.5rem)] sm:w-[calc(100vw-3rem)] md:w-[46rem] lg:w-[58rem] xl:w-[70rem]",
          "max-w-[calc(100vw-1.5rem)]",
          "h-[calc(100dvh-2rem)] sm:h-[calc(100dvh-3rem)] md:h-[40rem] lg:h-[44rem] xl:h-[48rem]",
          "max-h-[calc(100dvh-2rem)] md:max-h-[calc(100dvh-4rem)]",
          "overflow-hidden"
        )}
      >
        <div className="bg-surface dark:bg-dark-800 rounded-2xl w-full h-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="h-[3px] bg-gradient-to-r from-blue-500 to-indigo-500" />
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-line-subtle dark:border-dark-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <FlaskConical className="w-6 h-6 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <DialogTitle id="ab-test-modal-title" className="text-xl font-semibold text-content">
                  A/B Testing for {campaign.name}
                </DialogTitle>
                <DialogDescription className="text-sm text-content-secondary">
                  Manage and analyze A/B tests for this campaign
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {pendingDecisions.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {pendingDecisions.length} decision{pendingDecisions.length !== 1 ? 's' : ''} pending
                  </span>
                </div>
              )}
              <button aria-label="Close"
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-content-muted" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex-shrink-0 border-b border-line overflow-x-auto no-scrollbar">
            <nav className="flex px-6">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-3 px-4 border-b-2 font-medium text-sm ${activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
              >
                Overview ({campaignTests.length})
              </button>
              <button
                onClick={() => setActiveTab('active')}
                className={`py-3 px-4 border-b-2 font-medium text-sm ${activeTab === 'active'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
              >
                Active Tests ({activeTests.length})
              </button>
              <button
                onClick={() => setActiveTab('results')}
                className={`py-3 px-4 border-b-2 font-medium text-sm ${activeTab === 'results'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
              >
                Results ({completedTests.length})
              </button>
              <button
                onClick={() => setActiveTab('decisions')}
                className={`py-3 px-4 border-b-2 font-medium text-sm ${activeTab === 'decisions'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                    : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
              >
                AI Decisions ({campaignDecisions.length})
              </button>
            </nav>
          </div>

          {/* Content — fills whatever the frame leaves and scrolls. Was
              `max-h-[60vh]`, a second, unrelated cap that could end well short
              of the panel's own bottom and leave dead space beneath it. */}
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Distribution, but only once there is something to distribute.
                    Four tiles reading 0 above the line "No A/B tests created
                    yet" stated the same fact five times and pushed the one
                    useful control below the fold. */}
                {campaignTests.length > 0 && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Play className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                          <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Active</span>
                        </div>
                        <div className="text-2xl font-bold text-blue-900 dark:text-blue-300">{activeTests.length}</div>
                      </div>
                      <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
                          <span className="text-sm font-medium text-green-900 dark:text-green-300">Completed</span>
                        </div>
                        <div className="text-2xl font-bold text-green-900 dark:text-green-300">{completedTests.length}</div>
                      </div>
                      <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Eye className="w-4 h-4 text-yellow-600 dark:text-yellow-300" />
                          <span className="text-sm font-medium text-yellow-900 dark:text-yellow-300">Review</span>
                        </div>
                        <div className="text-2xl font-bold text-yellow-900 dark:text-yellow-300">{awaitingReviewTests.length}</div>
                      </div>
                      <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="w-4 h-4 text-content-secondary" />
                          <span className="text-sm font-medium text-content">Draft</span>
                        </div>
                        <div className="text-2xl font-bold text-content">{draftTests.length}</div>
                      </div>
                    </div>

                  </>
                )}

                {/* Nothing yet, and the form is closed: say what a test would do
                    for this campaign and offer the one action worth taking.
                    "No A/B tests created yet" told the user the state without
                    telling them what to do about it, or why they would. */}
                {campaignTests.length === 0 && !showCreateForm && (
                  <EmptyState
                    bare
                    icon={FlaskConical}
                    variant="empty"
                    title={`No A/B tests for ${campaign.name} yet`}
                    description="Send two versions of this campaign to slices of your audience — a different subject line, send time or layout — and Merko reports which one performed better."
                    primaryAction={{
                      label: 'Create your first test',
                      onClick: () => setShowCreateForm(true),
                    }}
                    hints={[
                      { icon: Mail, title: 'Subject line', description: 'The most common thing to test, and the quickest to read a result on.' },
                      { icon: Clock, title: 'Send time', description: 'Same email, two delivery windows.' },
                      { icon: Target, title: 'Content', description: 'Two layouts or calls to action, one audience.' },
                    ]}
                  />
                )}

                {/* Create New Test — hidden while the empty state above is
                    showing, which already offers this action. Two "create"
                    buttons stacked is how the old layout read. */}
                {(campaignTests.length > 0 || showCreateForm) && (
                <div className="border border-line rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-content">Create New A/B Test</h3>
                    <button
                      onClick={() => setShowCreateForm(!showCreateForm)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {showCreateForm ? 'Cancel' : 'Create Test'}
                    </button>
                  </div>

                  {showCreateForm && (
                    <div className="space-y-4 pt-4 border-t border-line">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Test Name
                          </label>
                          <input
                            type="text"
                            value={newTestConfig.name}
                            onChange={(e) => setNewTestConfig(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter test name"
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Test Type
                          </label>
                          {/* Shared Select. The native one opened an
                              operating-system list — grey on dark-mode
                              machines even inside this light panel, because a
                              className styles the closed field and nothing
                              else. */}
                          <Select
                            value={newTestConfig.type}
                            onValueChange={(v) => setNewTestConfig(prev => ({ ...prev, type: v }))}
                          >
                            <SelectTrigger className="w-full" aria-label="Test type">
                              <SelectValue placeholder="Choose what to test" />
                            </SelectTrigger>
                            <SelectContent>
                              {getTestTypeOptions().map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Description
                        </label>
                        <textarea
                          value={newTestConfig.description}
                          onChange={(e) => setNewTestConfig(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe what you want to test"
                          rows={3}
                          className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Traffic Split (%)
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={newTestConfig.trafficSplit[0]}
                              onChange={(e) => {
                                const value = parseInt(e.target.value);
                                setNewTestConfig(prev => ({
                                  ...prev,
                                  trafficSplit: [value, 100 - value]
                                }));
                              }}
                              min="10"
                              max="90"
                              className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <span className="text-content-muted">/</span>
                            <input
                              type="number"
                              value={newTestConfig.trafficSplit[1]}
                              readOnly
                              className="w-full px-3 py-2 border border-line-strong rounded-lg bg-gray-50 dark:bg-dark-700"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Duration (days)
                          </label>
                          <input
                            type="number"
                            value={newTestConfig.duration}
                            onChange={(e) => setNewTestConfig(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                            min="1"
                            max="30"
                            className="w-full px-3 py-2 border border-line-strong rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Success Metric
                          </label>
                          <Select
                            value={newTestConfig.successMetric}
                            onValueChange={(v) => setNewTestConfig(prev => ({ ...prev, successMetric: v }))}
                          >
                            <SelectTrigger className="w-full" aria-label="Success metric">
                              <SelectValue placeholder="Choose a metric" />
                            </SelectTrigger>
                            <SelectContent>
                              {getSuccessMetricOptions().map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-3 pt-4">
                        <button
                          onClick={() => setShowCreateForm(false)}
                          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleCreateTest}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Create Test
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* Recent Tests */}
                {campaignTests.length > 0 && (
                  <div>
                    <h3 className="font-medium text-content mb-4">Recent Tests</h3>
                    <div className="space-y-3">
                      {campaignTests.slice(0, 3).map(test => (
                        <div key={test.id} className="border border-line rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <FlaskConical className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                              <span className="font-medium text-content">{test.name}</span>
                              <ABTestStatusBadge test={test} size="small" />
                            </div>
                            <button
                              onClick={() => onViewTestDetails(test)}
                              className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                            >
                              View Details
                            </button>
                          </div>
                          {test.metrics && (
                            <ABTestResultsSummary test={test} compact={true} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Active Tests Tab */}
            {activeTab === 'active' && (
              <div className="space-y-4">
                {activeTests.length > 0 ? (
                  activeTests.map(test => (
                    <div key={test.id} className="border border-blue-200 dark:border-blue-800/50 rounded-lg p-6 bg-blue-50 dark:bg-blue-900/30">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Play className="w-5 h-5 text-blue-600 dark:text-blue-300" />
                          <div>
                            <h3 className="font-medium text-blue-900 dark:text-blue-300">{test.name}</h3>
                            <p className="text-sm text-blue-700 dark:text-blue-300">
                              Started {new Date(test.startedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <ABTestStatusBadge test={test} />
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => onViewTestDetails(test)}
                            className="px-3 py-1 text-sm text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg"
                          >
                            View Details
                          </button>
                          {test.winner && onImplementWinner && (
                            <button
                              onClick={() => onImplementWinner(test.id, test.winner)}
                              className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                            >
                              Implement Winner
                            </button>
                          )}
                        </div>
                      </div>
                      <ABTestResultsSummary test={test} />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-content-muted">
                    <Play className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No active A/B tests</p>
                    <p className="text-sm">Create a new test to start optimizing this campaign</p>
                  </div>
                )}
              </div>
            )}

            {/* Results Tab */}
            {activeTab === 'results' && (
              <div className="space-y-4">
                {completedTests.length > 0 ? (
                  completedTests.map(test => (
                    <div key={test.id} className="border border-green-200 dark:border-green-800/50 rounded-lg p-6 bg-green-50 dark:bg-green-900/30">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-300" />
                          <div>
                            <h3 className="font-medium text-green-900 dark:text-green-300">{test.name}</h3>
                            <p className="text-sm text-green-700 dark:text-green-300">
                              Completed {new Date(test.completedAt).toLocaleDateString()}
                            </p>
                          </div>
                          <ABTestStatusBadge test={test} />
                        </div>
                        <button
                          onClick={() => onViewTestDetails(test)}
                          className="px-3 py-1 text-sm text-green-600 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg"
                        >
                          View Full Results
                        </button>
                      </div>
                      <ABTestResultsSummary test={test} />
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-content-muted">
                    <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No completed A/B tests</p>
                    <p className="text-sm">Test results will appear here once tests are completed</p>
                  </div>
                )}
              </div>
            )}

            {/* AI Decisions Tab */}
            {activeTab === 'decisions' && (
              <AIAgentDecisionTrail
                campaign={campaign}
                decisions={campaignDecisions}
                collaborations={collaborations}
                agents={aiAgents}
                onApproveDecision={onApproveDecision}
                onRejectDecision={onRejectDecision}
                onViewDecisionDetails={(decision) => console.log('View decision details:', decision)}
              />
            )}
          </div>

          {/* Footer. Restored — converting this to a Dialog dropped it, and with
              it the Close button. It no longer repeats "No A/B tests created
              yet": the empty state above says that, and saying it twice on one
              screen is what made the panel feel like it was stalling. The count
              appears only when there is a count. */}
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-line bg-surface-muted">
            <div className="text-sm text-content-muted tabular-nums">
              {campaignTests.length > 0
                ? `${campaignTests.length} test${campaignTests.length !== 1 ? 's' : ''} total`
                : ''}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-ui font-semibold bg-surface border border-line text-content-secondary hover:bg-surface-hover hover:text-content transition-colors focus-ring"
            >
              Close
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignABTestModal;